import { getUniCandidates } from '@/lib/mockCandidates';
import { getCodeLabel } from '@/lib/mockCodes';
import { getMockResults, parseResult } from '@/lib/mockResults';
import {
  Chart,
  ChartResult,
  ResultsModel,
  StructureType,
  VariableModel,
} from '@/lib/model';
import { useMainContext } from '@/services/context';
import * as React from 'react';
import { ResultsComponent } from './ResultsComponent';

export const Results = () => {
  const { votingFeatures, resetAllFeatures, addressFeature } = useMainContext();

  const onAddressClick = () => {
    resetAllFeatures();
  };

  // Features
  const cameraData = votingFeatures?.find(i => !!i.properties?.CU20_COD);
  const senateData = votingFeatures?.find(i => !!i.properties?.SU20_COD);

  // Mock Data
  const cameraMockData = getMockResults(
    StructureType.CAMERA,
    cameraData?.properties?.OBJECTID || 0
  );
  const senateMockData = getMockResults(
    StructureType.SENATE,
    senateData?.properties?.OBJECTID || 0
  );

  // City name
  const placeName = addressFeature?.place_name_it || '';
  const placeType = addressFeature?.place_type
    ? addressFeature?.place_type[0]
    : '';
  const localCommunity = addressFeature?.context || [];
  const cityName =
    placeType === 'address'
      ? `${localCommunity[localCommunity.length === 5 ? 2 : 1].text_it} (${
          localCommunity[0].text_it
        })`
      : placeName;

  // Full address
  const fullAddress = addressFeature?.place_name || '';

  // PREVIOUS ELECTION RESULTS
  // Empty Previous Election Chart Model
  const prevElectionChartEmpty: Chart[] = [
    {
      code: 'T_CDX', // totale centrodestra
      result: 0,
      fillColor: '#1F8CCB',
      innerResults: [
        {
          code: 'P1',
          result: 0,
        },
        {
          code: 'P2',
          result: 0,
        },
        {
          code: 'P3',
          result: 0,
        },
        {
          code: 'P4',
          result: 0,
        },
      ],
    },
    {
      code: 'T_CSX', // totale centro sinistra
      result: 0,
      fillColor: '#AF0611',
      innerResults: [
        {
          code: 'P5',
          result: 0,
        },
        {
          code: 'P6',
          result: 0,
        },
        {
          code: 'P7',
          result: 0,
        },
        {
          code: 'P8',
          result: 0,
        },
      ],
    },
    {
      code: 'P9', // mov. 5 stelle
      result: 0,
      fillColor: '#E39D22',
    },
    {
      code: 'P10', // Liberi e Uguali
      result: 0,
      fillColor: '#B3390E',
    },
    {
      code: 'P11', // Altri
      result: 0,
      fillColor: '#909090',
    },
  ];

  const fillInPrevElectionResults = (type: StructureType) => {
    const mockData =
      type === StructureType.CAMERA ? cameraMockData : senateMockData;

    return prevElectionChartEmpty.map(chart => ({
      ...chart,
      label: getCodeLabel(type, chart.code),
      result: parseResult(mockData[chart.code]),
      ...(chart.innerResults
        ? {
            innerResults: chart.innerResults.map((result: ChartResult) => ({
              ...result,
              label: getCodeLabel(type, result.code),
              result: parseResult(mockData[result.code]),
            })),
          }
        : {}),
    }));
  };

  // Camera Prev. Election Chart
  const prevElectionChartCamera = fillInPrevElectionResults(
    StructureType.CAMERA
  );

  // Senate Prev. Election Chart
  const prevElectionChartSenate = fillInPrevElectionResults(
    StructureType.SENATE
  );

  // CANDIDATES
  // Camera candidates Uni
  const candidatesUniCamera = getUniCandidates(
    StructureType.CAMERA,
    cameraData?.properties?.CU20_DEN || ''
  );

  // Senate candidates Uni
  const candidatesUniSenate = getUniCandidates(
    StructureType.SENATE,
    senateData?.properties?.SU20_DEN || ''
  );

  // DEMOGRAPHIC VARIABLES
  const demographicVarsEmpty: VariableModel[] = [
    {
      code: 'OVR',
      label: 'Over 65',
      description: '(in % sulla popolazione, ISTAT)',
      frontBarLabel: '0%',
      frontBarPercentage: 0,
      backBarLabel: '28.2%',
      backBarPercentage: 28.2,
    },
    {
      code: 'LRT',
      label: 'Laureati',
      description: '(in % sulla popolazione maggiorenne, ISTAT)',
      frontBarLabel: '0%',
      frontBarPercentage: 0,
      backBarLabel: '15.7%',
      backBarPercentage: 15.7,
    },
    {
      code: 'DSC',
      label: 'Tasso di disoccupazione',
      description: '(in % sulle forze di lavoro, ISTAT)',
      frontBarLabel: '0%',
      frontBarPercentage: 0,
      backBarLabel: '13.1%',
      backBarPercentage: 13.1,
    },
    {
      code: 'STR',
      label: 'Cittadini stranieri',
      description: '(in % sulla popolazione, ISTAT)',
      frontBarLabel: '0%',
      frontBarPercentage: 0,
      backBarLabel: '8.8%',
      backBarPercentage: 8.8,
    },
    {
      code: 'RDD',
      label: 'Reddito imponibile medio',
      description: "(anno d'imposta 2020, MEF)",
      frontBarLabel: '0',
      frontBarPercentage: 0,
      backBarLabel: '0',
      backBarPercentage: 0,
    },
  ];

  const fillInDemVars = (type: StructureType) => {
    const mockData =
      type === StructureType.CAMERA ? cameraMockData : senateMockData;

    return demographicVarsEmpty.map(dV => {
      let frontBarPercentage = parseResult(mockData[dV.code]) || 0;
      let frontBarLabel = `${frontBarPercentage}%`;

      let backBarPercentage = dV.backBarPercentage;
      let backBarLabel = dV.backBarLabel;

      // different values for 'reddito'
      if (dV.code === 'RDD') {
        frontBarLabel = `${frontBarPercentage.toLocaleString('it', {
          minimumFractionDigits: 0,
        })}€`;
        backBarPercentage = 20851;
        backBarLabel = '20.851€';

        let relation = backBarPercentage / frontBarPercentage;
        if (backBarPercentage > frontBarPercentage) {
          frontBarPercentage = (1 / relation) * 100;
          backBarPercentage = 100; // forcing back bar to be 100%
        } else {
          backBarPercentage = relation * 100;
          frontBarPercentage = 100; // forcing front bar to be 100%
        }
      } else {
        // we're multiplying by 2 as percentages are always below 50%
        frontBarPercentage = frontBarPercentage * 2;
        backBarPercentage = backBarPercentage * 2;
      }

      return {
        ...dV,
        backBarPercentage,
        backBarLabel,
        frontBarLabel,
        frontBarPercentage,
      };
    });
  };

  // Camera Demographic Vars
  const cameraDemographicVars = fillInDemVars(StructureType.CAMERA);

  // Senate Demographic Vars
  const senateDemographicVars = fillInDemVars(StructureType.SENATE);

  // Whole model
  const resultsData: ResultsModel = {
    cityName,
    fullAddress,
    structures: [
      {
        fillColor: '#FEC1D9',
        tabLabel: 'Camera',
        collegeName: `${cameraData?.properties?.CU20_DEN}`,
        candidates: candidatesUniCamera,
        mapSettings: {
          coordinates: cameraData?.geometry?.coordinates || [0, 0],
          sourceId: 'aurelianv.Circo_Camera_2020_tileset',
          sourceUrl: 'mapbox://aurelianv.Circo_Camera_2020_tileset',
          layerId: 'CU20_COD',
          sourceLayer: 'CAMERA_CollegiUN_2020',
          featureId: cameraData?.id || 0,
        },
        prevElectionChart: prevElectionChartCamera,
        demographicDesc: cameraMockData.PST,
        demographicVars: cameraDemographicVars,
      },
      {
        fillColor: '#FFF171',
        tabLabel: 'Senato',
        collegeName: `${senateData?.properties?.SU20_DEN}`,
        candidates: candidatesUniSenate,
        mapSettings: {
          coordinates: senateData?.geometry?.coordinates || [0, 0],
          sourceId: 'aurelianv.Circo_Camera_2020_tileset',
          sourceUrl: 'mapbox://aurelianv.Circo_Camera_2020_tileset',
          layerId: 'SU20_COD',
          sourceLayer: 'SENATO_CollegiUN_2020',
          featureId: senateData?.id || 0,
        },
        prevElectionChart: prevElectionChartSenate,
        demographicDesc: senateMockData.PST,
        demographicVars: senateDemographicVars,
      },
    ],
  };

  // Share widget url
  const shareUrl = 'https://bit.ly/trovacollegio';

  return (
    <ResultsComponent
      {...resultsData}
      shareUrl={shareUrl}
      onAddressClick={onAddressClick}
    />
  );
};
